import type {
  FullProfile,
  ProfileSummary,
  SwipeRequest,
  SwipeResponse,
  Match,
  Message,
  BootstrapResponse,
  PaginatedResponse,
  OnboardingData,
  VerificationStatusResponse,
  StartTestResponse,
  TestDataResponse,
  SubmitAnswersRequest,
  SubmitAnswersResponse,
  TestResultResponse,
  CardResponse,
  SwipeCreateRequest,
  SwipeResponseData,
  LikesResponse,
  MyLikesResponse,
  RemoveLikeRequest,
  RemoveLikeResponse,
  MatchResponse,
  MessageResponse,
  NotificationCounters,
  SubscriptionStatusResponse,
  BasicFilters,
  BasicFiltersResponse,
  MessageCreate,
  MessageUpdate,
  MessageReport,
} from '@/types';
import { BaseApiClient } from './client';
import { TelegramUtils } from '@/utils/telegram';

interface AuthResponse {
  token: string;
  user: any;
  expires_at: string;
}

interface DictionaryItem {
  id: number;
  name: string;
  icon?: string;
}

interface Specialization extends DictionaryItem {
  description?: string;
}

interface TechStackItem extends DictionaryItem {
  category?: string;
}

/**
 * Real API client for production
 */
export class RealApiClient extends BaseApiClient {
  constructor() {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8002/api';
    super(baseUrl);
  }

  /**
   * Authenticate via Telegram or create test user
   */
  async bootstrap(initData?: string): Promise<BootstrapResponse> {
    try {
      let authData: AuthResponse | null = null;

      // Check if we have existing token
      const savedToken = localStorage.getItem('auth_token');
      if (savedToken) {
        try {
          console.log('🔑 RealApiClient.bootstrap: found saved token, setting it before validation');
          // Set token BEFORE trying to use it
          this.setToken(savedToken);
          
          // Verify token is still valid
          console.log('✓ RealApiClient.bootstrap: validating token by fetching profile');
          const profile = await this.getMyProfile();
          console.log('✅ RealApiClient.bootstrap: token is valid, returning profile');
          return this.createBootstrapResponse(profile);
        } catch (error: any) {
          console.warn('⚠️ RealApiClient.bootstrap: token validation failed', error);
          
          // Check if it's a 401 error (token expired)
          const is401 = error.status === 401 || error.message?.includes('401') || error.message?.includes('Unauthorized');
          
          if (is401 && (initData || TelegramUtils.isInTelegram)) {
            console.log('🔄 Token expired (401), attempting to refresh via /auth/telegram');
            
            // Try to get new token via Telegram auth
            try {
              const telegramInitData = initData || TelegramUtils.getInitData();
              if (telegramInitData) {
                authData = await this.post<AuthResponse>('/auth/telegram', {
                  telegram_init_data: telegramInitData,
                });
                
                if (authData) {
                  console.log('✅ Successfully refreshed token via /auth/telegram');
                  
                  // Save new token
                  this.setToken(authData.token);
                  localStorage.setItem('auth_token', authData.token);
                  localStorage.setItem('token_expires', authData.expires_at);
                  
                  // Retry getting profile with new token
                  try {
                    const profile = await this.getMyProfile();
                    console.log('✅ Successfully fetched profile with new token');
                    return this.createBootstrapResponse(profile);
                  } catch (profileError) {
                    console.error('❌ Failed to fetch profile even with new token:', profileError);
                    // Continue with normal flow using authData
                    const profile = this.mapUserToProfile(authData.user);
                    return this.createBootstrapResponse(profile);
                  }
                }
              }
            } catch (refreshError) {
              console.error('❌ Failed to refresh token:', refreshError);
            }
          }
          
          // Token refresh failed or not applicable, clear and continue
          console.log('🗑️ Clearing expired/invalid token');
          localStorage.removeItem('auth_token');
          this.token = undefined;
        }
      }

      // Try Telegram auth if in Telegram and we don't have auth data yet
      if (!authData && initData && TelegramUtils.isInTelegram) {
        try {
          authData = await this.post<AuthResponse>('/auth/telegram', {
            telegram_init_data: initData,
          });
        } catch (error) {
          console.error('Telegram auth failed:', error);
        }
      }

      // If no auth data, return mock bootstrap for now
      if (!authData) {
        // User needs to select gender first
        // Return minimal bootstrap
        return {
          me: null as any,
          settings: {
            maxPhotos: 5,
            maxBio: 500,
            maxDistance: 100,
            ageRange: [18, 55],
            dailyLikeLimit: 100,
            superLikeLimit: 5,
          },
          features: {
            superLikes: true,
            readReceipts: true,
            aiMatch: true,
            voiceMessages: false,
            videoCall: false,
          },
        };
      }

      // Save token (if we got here without saving it already)
      if (!localStorage.getItem('auth_token')) {
        this.setToken(authData.token);
        localStorage.setItem('auth_token', authData.token);
        localStorage.setItem('token_expires', authData.expires_at);
      }

      const profile = this.mapUserToProfile(authData.user);
      return this.createBootstrapResponse(profile);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create test user (development only)
   */
  async createTestUser(gender: 'male' | 'female'): Promise<AuthResponse> {
    const response = await this.post<AuthResponse>(`/auth/test-user?gender=${gender}`);
    
    // Save token
    this.setToken(response.token);
    localStorage.setItem('auth_token', response.token);
    localStorage.setItem('token_expires', response.expires_at);
    
    return response;
  }

  /**
   * Get dictionaries
   */
  async getLifestyleTags(): Promise<DictionaryItem[]> {
    const response = await this.get<{ lifestyle_tags: DictionaryItem[] }>('/dictionaries/lifestyle-tags');
    return response.lifestyle_tags;
  }

  async getHobbies(): Promise<DictionaryItem[]> {
    const response = await this.get<{ hobbies: DictionaryItem[] }>('/dictionaries/hobbies');
    return response.hobbies;
  }

  async getMeetingGoals(): Promise<DictionaryItem[]> {
    const response = await this.get<{ meeting_goals: DictionaryItem[] }>('/dictionaries/meeting-goals');
    return response.meeting_goals;
  }

  async getSpecializations(includeTechStack: boolean = false): Promise<Specialization[]> {
    const url = includeTechStack 
      ? '/dictionaries/specializations?include_tech_stack=true' 
      : '/dictionaries/specializations';
    const response = await this.get<{ specializations: Specialization[] }>(url);
    return response.specializations;
  }

  async getTechStackForSpecialization(specializationId: number): Promise<TechStackItem[]> {
    const response = await this.get<{ tech_stack: TechStackItem[] }>(`/dictionaries/tech-stack/${specializationId}`);
    return response.tech_stack;
  }

  /**
   * Upload photo
   */
  async uploadPhoto(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('position', '1');
    formData.append('is_main', 'true');

    const response = await fetch(`${this.baseUrl}/profile/photos`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload photo');
    }

    const data = await response.json();
    return { url: data.photo_url };
  }
  
  /**
   * Upload photo only (for chat messages) - doesn't add to profile
   */
  async uploadPhotoOnly(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${this.baseUrl}/profile/photos/upload-only`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload photo');
    }

    const data = await response.json();
    return { url: data.photo_url || data.url };
  }

  /**
   * Upload photos bulk
   */
  async uploadPhotosBulk(photoUrls: string[]): Promise<any> {
    const photos = photoUrls.map((url, index) => ({
      photo_url: url,
      position: index + 1,
      is_main: index === 0,
    }));

    return await this.post('/profile/photos/bulk', {
      photos,
      replace_all: true,
    });
  }

  /**
   * Update basic profile info
   */
  async updateBasicInfo(data: {
    first_name?: string;
    birthdate?: string;
    bio?: string;
    city?: string;
    country?: string;
    looking_for?: 'male' | 'female' | 'all';
    meeting_goal_ids?: number[];
  }): Promise<any> {
    return await this.put('/profile/basic', data);
  }

  /**
   * Update IT info and hobbies
   */
  async updateITInfo(data: {
    specializations?: Array<{ specialization_id: number; level?: string }>;
    tech_stack_ids?: number[];
    hobby_ids?: number[];
  }): Promise<any> {
    return await this.put('/profile/it-info', data);
  }

  /**
   * Update gender
   */
  async updateGender(gender: 'male' | 'female'): Promise<any> {
    return await this.put('/profile/gender', { gender });
  }

  /**
   * Update lifestyle tag
   */
  async updateLifestyle(lifestyleTagId: number): Promise<any> {
    return await this.put(`/profile/lifestyle?lifestyle_tag_id=${lifestyleTagId}`);
  }

  /**
   * Get my profile
   */
  async getMyProfile(): Promise<FullProfile> {
    const user = await this.get<any>('/profile/me');
    return this.mapUserToProfile(user);
  }

  /**
   * Get registration status
   */
  async getRegistrationStatus(): Promise<any> {
    return await this.get('/profile/registration-status');
  }

  // Helper methods
  private createBootstrapResponse(profile: FullProfile): BootstrapResponse {
    return {
      me: profile,
      settings: {
        maxPhotos: 5,
        maxBio: 500,
        maxDistance: 100,
        ageRange: [18, 55],
        dailyLikeLimit: 100,
        superLikeLimit: 5,
      },
      features: {
        superLikes: true,
        readReceipts: true,
        aiMatch: true,
        voiceMessages: false,
        videoCall: false,
      },
    };
  }

  private mapUserToProfile(user: any): FullProfile {
    return {
      id: user.id.toString(),
      name: user.first_name || 'User',
      age: user.age || 25,
      gender: user.gender,
      status: user.status,
      is_it: user.is_it,
      looking_for: user.looking_for,
      role: user.specializations?.[0]?.name,
      company: user.company,
      level: user.verification_badge_level,
      itTestScore: user.verification_score,
      verified: user.is_verified,
      techStack: user.tech_stack?.map((t: any) => t.name) || [],
      photos: user.photos?.map((p: any) => p.photo_url) || [],
      bio: user.bio,
      birthdate: user.birthdate,
      city: user.city,
      country: user.country,
      lifestyle: user.hobbies?.map((h: any) => h.name) || [],
      hobbyIds: user.hobbies?.map((h: any) => h.id) || [],
      lifestyleTagId: user.lifestyle_tag?.id,
      desiredLifestyle: user.lifestyle_tag ? `${user.lifestyle_tag.icon} ${user.lifestyle_tag.name}` : undefined,
      meetingGoals: user.meeting_goals || [],
      meetingGoalIds: user.meeting_goals?.map((g: any) => g.id) || [],
      specializations: user.specializations || [],
      tech_stack: user.tech_stack || [],
      location: {
        city: user.city || '',
        country: user.country || '',
      },
      onlineStatus: user.online_status,
      lastActiveAt: user.last_active_at,
    };
  }

  // Finalize onboarding and return current profile
  // Server already persists pieces via specific endpoints used earlier
  // Here we simply fetch the fresh profile snapshot
  async createProfile(data: Partial<OnboardingData>): Promise<FullProfile> {
    const profile = await this.getMyProfile();
    return profile;
  }

  async updateProfile(data: Partial<FullProfile>): Promise<FullProfile> {
    await this.updateBasicInfo({
      bio: data.bio,
      city: data.location?.city,
      country: data.location?.country,
    });
    return await this.getMyProfile();
  }

  async getProfile(id: string): Promise<FullProfile> {
    const user = await this.get<any>(`/profile/${id}`);
    return this.mapUserToProfile(user);
  }

  async deletePhoto(photoUrl: string): Promise<void> {
    // Extract photo ID from URL or use existing implementation
    throw new Error('Not implemented');
  }

  async getFeedProfiles(limit?: number, cursor?: string): Promise<PaginatedResponse<ProfileSummary>> {
    // Will be implemented when connecting to cards API
    throw new Error('Not implemented - use mock for now');
  }

  async swipeProfile(request: SwipeRequest): Promise<SwipeResponse> {
    throw new Error('Not implemented - use mock for now');
  }

  async getMatch(matchId: string): Promise<Match> {
    const res = await this.get<MatchResponse>(`/matches/${Number(matchId)}`);
    // Map MatchResponse -> Match (domain)
    const mapped: Match = {
      id: res.id.toString(),
      users: [
        {
          id: res.matched_user.id.toString(),
          name: res.matched_user.first_name,
          age: res.matched_user.age,
          techStack: [],
          photos: res.matched_user.main_photo ? [res.matched_user.main_photo] : [],
        },
        {
          // current user placeholder; frontend doesn't use second user fields critically here
          id: 'me',
          name: 'You',
          age: 0,
          techStack: [],
          photos: [],
        },
      ],
      createdAt: res.created_at,
      lastMessage: res.last_message
        ? {
            id: res.last_message.id.toString(),
            matchId: res.id.toString(),
            senderId: res.last_message.sender_id.toString(),
            text: res.last_message.content,
            timestamp: res.last_message.created_at,
            read: res.last_message.is_read,
            type: 'text',
          }
        : undefined,
      unreadCount: res.unread_count,
    };
    return mapped;
  }

  async unmatch(matchId: string): Promise<void> {
    // Use unmatchUser
    await this.unmatchUser(Number(matchId));
  }

  async starMatch(matchId: string, starred: boolean): Promise<void> {
    await this.post(`/matches/${Number(matchId)}/star`, { starred });
  }

  async getMessages(matchId: string, limit?: number, cursor?: string): Promise<PaginatedResponse<Message>> {
    // Use getMatchMessages and convert response
    const offset = cursor ? parseInt(cursor) : 0;
    const messages = await this.getMatchMessages(Number(matchId), limit || 50, offset);
    
    return {
      items: messages.map(msg => ({
        id: msg.id.toString(),
        matchId: matchId,
        senderId: msg.sender_id.toString(),
        text: msg.content,
        timestamp: msg.created_at,
        read: msg.is_read,
        type: 'text' as const,
      })),
      hasMore: messages.length === (limit || 50),
      nextCursor: messages.length > 0 ? String(offset + messages.length) : undefined,
    };
  }

  async sendMessage(matchId: string, text: string): Promise<Message> {
    // Use sendMatchMessage and convert response
    const response = await this.sendMatchMessage(Number(matchId), text);
    
    return {
      id: response.id.toString(),
      matchId: matchId,
      senderId: response.sender_id.toString(),
      text: response.content,
      timestamp: response.created_at,
      read: response.is_read,
      type: 'text',
    };
  }

  async markMessagesRead(matchId: string, messageIds: string[]): Promise<void> {
    // Fallback implementation: mark each message individually
    for (const id of messageIds) {
      try {
        await this.markMessageAsRead(Number(id));
      } catch (e) {
        // Continue marking others; log for visibility
        console.warn('markMessagesRead: failed to mark', id, e);
      }
    }
  }

  async reportUser(userId: string, reason: string): Promise<void> {
    console.log('🌐 RealAPI: reportUser called', { userId, reason });
    try {
      const result = await this.post('/report', {
        reported_user_id: Number(userId),
        reason: reason
      });
      console.log('🌐 RealAPI: reportUser response', result);
      return result;
    } catch (error) {
      console.error('🌐 RealAPI: reportUser error', error);
      throw error;
    }
  }

  async blockUser(userId: string): Promise<void> {
    console.log('🌐 RealAPI: blockUser called', { userId });
    try {
      const result = await this.post('/block', {
        blocked_user_id: Number(userId)
      });
      console.log('🌐 RealAPI: blockUser response', result);
      return result;
    } catch (error) {
      console.error('🌐 RealAPI: blockUser error', error);
      throw error;
    }
  }

  async getBlockedUsers(): Promise<any> {
    return await this.get('/blocked');
  }

  async unblockUser(userId: string): Promise<void> {
    await this.delete(`/block/${userId}`);
  }

  async rewind(): Promise<ProfileSummary | null> {
    try {
      await this.post<{ success: boolean; message?: string }>(`/rewind`);
      return null;
    } catch (e) {
      throw e;
    }
  }

  async boost(): Promise<{ expiresAt: string }> {
    const res = await this.post<{ expiresAt?: string }>(`/boost`);
    return { expiresAt: res?.expiresAt || new Date(Date.now() + 3600_000).toISOString() };
  }

  async updatePreferences(preferences: any): Promise<void> {
    await this.put(`/profile/preferences`, preferences);
  }

  async deleteAccount(): Promise<void> {
    console.log('🔥 RealApiClient.deleteAccount() called');
    console.log('🔥 Current token:', this.token ? 'EXISTS' : 'MISSING');
    console.log('🔥 Sending DELETE /profile/me');
    
    try {
      const result = await this.delete('/profile/me');
      console.log('✅ DELETE /profile/me response:', result);
      return result;
    } catch (error) {
      console.error('❌ DELETE /profile/me failed:', error);
      throw error;
    }
  }

  // ========== IT Verification API ==========

  /**
   * Get verification status
   */
  async getVerificationStatus(): Promise<VerificationStatusResponse> {
    return await this.get<VerificationStatusResponse>('/verification/status');
  }

  /**
   * Start new test generation
   */
  async startVerificationTest(data: { 
    specialization_id: number; 
    declared_level: string 
  }): Promise<StartTestResponse> {
    return await this.post<StartTestResponse>('/verification/start', data);
  }

  /**
   * Get test data (for polling)
   */
  async getTestData(testId: number): Promise<TestDataResponse> {
    return await this.get<TestDataResponse>(`/verification/test/${testId}`);
  }

  /**
   * Submit test answers
   */
  async submitTestAnswers(data: SubmitAnswersRequest): Promise<SubmitAnswersResponse> {
    return await this.post<SubmitAnswersResponse>('/verification/submit', data);
  }

  /**
   * Get test result (for polling)
   */
  async getTestResult(testId: number): Promise<TestResultResponse> {
    return await this.get<TestResultResponse>(`/verification/result/${testId}`);
  }

  /**
   * Poll until test is ready (with questions)
   */
  async waitForTestQuestions(
    testId: number, 
    onProgress?: (attempt: number) => void,
    maxAttempts: number = 60,
    intervalMs: number = 3000
  ): Promise<TestDataResponse> {
    for (let i = 0; i < maxAttempts; i++) {
      const response = await this.getTestData(testId);

      if (response.status === 'ready' && response.questions) {
        return response;
      }

      if (response.status === 'failed') {
        throw new Error('Test generation failed');
      }

      if (onProgress) {
        onProgress(i + 1);
      }

      // Wait before next attempt
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }

    throw new Error('Timeout waiting for test questions');
  }

  /**
   * Poll until test result is ready
   */
  async waitForTestResult(
    testId: number,
    onProgress?: (attempt: number) => void,
    maxAttempts: number = 40,
    intervalMs: number = 3000
  ): Promise<TestResultResponse> {
    for (let i = 0; i < maxAttempts; i++) {
      const response = await this.getTestResult(testId);

      if (response.status === 'completed' && response.score !== undefined) {
        return response;
      }

      if (response.status === 'failed') {
        throw new Error('Test evaluation failed');
      }

      if (onProgress) {
        onProgress(i + 1);
      }

      // Wait before next attempt
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }

    throw new Error('Timeout waiting for test result');
  }

  // ========== Discovery & Swipes API ==========

  /**
   * Get discovery cards for swiping
   */
  async getDiscoveryCards(limit: number = 10): Promise<CardResponse[]> {
    return await this.get<CardResponse[]>(`/cards?limit=${limit}`);
  }

  /**
   * Swipe on a user (like/pass/superlike)
   */
  async swipeUser(data: SwipeCreateRequest): Promise<SwipeResponseData> {
    return await this.post<SwipeResponseData>('/swipe', data);
  }

  /**
   * Reset passed swipes to see profiles again
   */
  async resetPassSwipes(): Promise<{ success: boolean; message: string }> {
    return await this.post<{ success: boolean; message: string }>('/reset-pass-swipes');
  }

  /**
   * Rewind last swipe (Gold feature)
   */
  async rewindSwipe(): Promise<{ success: boolean; message: string }> {
    return await this.post<{ success: boolean; message: string }>('/rewind');
  }

  /**
   * Get users who liked you
   */
  async getLikes(): Promise<LikesResponse> {
    return await this.get<LikesResponse>('/likes');
  }

  // ========== Matches API ==========

  /**
   * Get all matches
   */
  async getMatches(): Promise<MatchResponse[]> {
    return await this.get<MatchResponse[]>('/matches');
  }

  /**
   * Unmatch with user
   */
  async unmatchUser(matchId: number): Promise<{ success: boolean; message: string }> {
    return await this.delete<{ success: boolean; message: string }>(`/matches/${matchId}`);
  }

  // ========== Messages API ==========

  /**
   * Get messages for a match
   */
  async getMatchMessages(matchId: number, limit: number = 50, offset: number = 0): Promise<MessageResponse[]> {
    return await this.get<MessageResponse[]>(`/matches/${matchId}/messages?limit=${limit}&offset=${offset}`);
  }

  /**
   * Send message in a match
   */
  async sendMatchMessage(matchId: number, content: string): Promise<MessageResponse> {
    return await this.post<MessageResponse>(`/matches/${matchId}/messages`, { content });
  }

  /**
   * Send message in a match (v2 with full support)
   */
  async sendMessageV2(matchId: number, data: MessageCreate): Promise<Message> {
    const response = await this.post<MessageResponse>(`/matches/${matchId}/messages`, data);
    return {
      id: response.id.toString(),
      matchId: matchId.toString(),
      senderId: response.sender_id.toString(),
      text: response.content,
      timestamp: response.created_at,
      read: response.is_read,
      type: 'text',
      photoUrl: response.photo_url,
      replyToMessageId: response.reply_to_message_id?.toString(),
      repliedMessage: response.replied_message 
        ? {
            id: response.replied_message.id.toString(),
            matchId: matchId.toString(),
            senderId: response.replied_message.sender_id.toString(),
            text: response.replied_message.content,
            timestamp: response.replied_message.created_at,
            read: response.replied_message.is_read,
            type: 'text',
            photoUrl: response.replied_message.photo_url,
            deletedAt: response.replied_message.deleted_at,
            editedAt: response.replied_message.edited_at,
            isEdited: response.replied_message.is_edited,
          }
        : null,
      deletedAt: response.deleted_at,
      editedAt: response.edited_at,
      isEdited: response.is_edited,
    };
  }

  /**
   * Edit message
   */
  async editMessage(messageId: number, content: string): Promise<Message> {
    const response = await this.put<MessageResponse>(`/messages/${messageId}`, { content });
    
    // Map to Message format - matchId will be updated by the hook
    return {
      id: response.id.toString(),
      matchId: '', // Will be filled by the hook
      senderId: response.sender_id.toString(),
      text: response.content,
      timestamp: response.created_at,
      read: response.is_read,
      type: 'text',
      photoUrl: response.photo_url,
      replyToMessageId: response.reply_to_message_id?.toString(),
      repliedMessage: response.replied_message 
        ? {
            id: response.replied_message.id.toString(),
            matchId: '',
            senderId: response.replied_message.sender_id.toString(),
            text: response.replied_message.content,
            timestamp: response.replied_message.created_at,
            read: response.replied_message.is_read,
            type: 'text',
            photoUrl: response.replied_message.photo_url,
            deletedAt: response.replied_message.deleted_at,
            editedAt: response.replied_message.edited_at,
            isEdited: response.replied_message.is_edited,
          }
        : null,
      deletedAt: response.deleted_at,
      editedAt: response.edited_at,
      isEdited: response.is_edited,
    };
  }

  /**
   * Delete message (soft delete)
   */
  async deleteMessage(messageId: number): Promise<{ success: boolean; message: string }> {
    return await this.delete<{ success: boolean; message: string }>(`/messages/${messageId}`);
  }

  /**
   * Report message
   */
  async reportMessage(messageId: number, reason: string): Promise<{ success: boolean; message: string; report_id: number }> {
    return await this.post<{ success: boolean; message: string; report_id: number }>('/messages/report', {
      message_id: messageId,
      reason
    });
  }

  /**
   * Mark message as read
   */
  async markMessageAsRead(messageId: number): Promise<{ success: boolean }> {
    return await this.put<{ success: boolean }>(`/messages/${messageId}/read`);
  }

  /**
   * Get notification counters (unread messages and unseen likes)
   */
  async getNotificationCounters(): Promise<NotificationCounters> {
    return await this.get<NotificationCounters>('/matches/notifications/counters');
  }

  /**
   * Get subscription status
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    return await this.get<SubscriptionStatusResponse>('/subscriptions/status');
  }

  // ========== My Likes (Outgoing Likes) API ==========

  /**
   * Get my outgoing likes
   */
  async getMyLikes(): Promise<MyLikesResponse> {
    return await this.get<MyLikesResponse>('/my-likes');
  }

  /**
   * Remove a like (unlike)
   */
  async removeLike(data: RemoveLikeRequest): Promise<RemoveLikeResponse> {
    return await this.post<RemoveLikeResponse>('/likes/remove', data);
  }

  // ========== Payments API ==========

  /**
   * Create payment invoice
   */
  async createPaymentInvoice(data: { invoice_type: string; metadata?: Record<string, any> }): Promise<any> {
    return await this.post('/payments/create-invoice', data);
  }

  /**
   * Check invoice status
   */
  async checkInvoiceStatus(invoiceId: number): Promise<any> {
    return await this.get(`/payments/check/${invoiceId}`);
  }

  /**
   * Get basic filters
   */
  async getBasicFilters(): Promise<BasicFiltersResponse> {
    return await this.get('/filters/basic');
  }

  /**
   * Update basic filters
   */
  async updateBasicFilters(filters: Partial<BasicFilters>): Promise<void> {
    await this.put('/filters/basic/update', filters);
  }

  /**
   * Reset basic filters to defaults
   */
  async resetBasicFilters(): Promise<void> {
    await this.post('/filters/basic/reset');
  }

  /**
   * Browse profiles with swipe_status (alternative feed)
   */
  async browseProfiles(limit: number = 10, lastId?: number): Promise<import('@/types').BrowseCardResponse[]> {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      if (lastId) {
        params.append('last_id', lastId.toString());
      }
      
      const response = await this.get<import('@/types').BrowseCardResponse[]>(`/browse?${params.toString()}`);
      return response || [];
    } catch (error: any) {
      console.error('Failed to browse profiles:', error);
      throw error;
    }
  }

  /**
   * Update swipe action (can change existing swipe)
   */
  async updateSwipe(targetUserId: number, action: 'like' | 'pass' | 'superlike', hide?: number): Promise<import('@/types').SwipeResponseData> {
    try {
      const payload: any = {
        target_user_id: targetUserId,
        action: action
      };
      
      if (hide !== undefined) {
        payload.hide = hide;
      }
      
      const response = await this.post<import('@/types').SwipeResponseData>('/swipe', payload);
      return response;
    } catch (error: any) {
      console.error('Failed to update swipe:', error);
      throw error;
    }
  }

  /**
   * Delete like completely (removes from history)
   */
  async deleteLike(targetUserId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.post<{ success: boolean; message: string }>('/likes/delete', {
        target_user_id: targetUserId
      });
      return response;
    } catch (error: any) {
      console.error('Failed to delete like:', error);
      throw error;
    }
  }
}
